// src/components/dashboard/ActionCard.js
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import { OptimizedImage } from '../../utils/imageManager';
import { DAILY_LEARN_HARD_CAP } from '../../utils/constants';

const ActionCard = ({ type, title, description, link, count, disabledText, vacationStartedAt, emailVerified, learnedToday = 0, showUpgradePrompt = false, disabledTooltip = null, suppressExtraStudy = false }) => {
  const onVacation = !!vacationStartedAt;
  const isEmailUnverified = !emailVerified;
  const isActionable = !onVacation && count > 0 && !isEmailUnverified;

  // Modern state configuration using CSS custom properties
  const getStateConfig = () => {
    if (onVacation) {
      return {
        state: 'vacation',
        ctaText: 'On Vacation',
        ctaIcon: '🏝️',
        textColor: 'text-white',
        gradientFrom: 'var(--color-session-vacation)',
        gradientTo: 'var(--color-session-vacation-accent)',
        bubbleStyle: {
          bubble1: { size: 80, className: 'top-0 right-0 translate-x-6 -translate-y-10' },
          bubble2: { size: 48, className: 'bottom-0 left-0 -translate-x-4 translate-y-6' },
        },
      };
    }

    if (isEmailUnverified && count > 0) {
      return {
        state: 'unverified',
        ctaText: 'Verify Email',
        ctaIcon: '📧',
        textColor: 'text-white',
        gradientFrom: 'var(--color-primary)',
        gradientTo: 'var(--color-secondary)',
        bubbleStyle: {
          bubble1: { size: 72, className: 'top-0 left-0 -translate-x-8 -translate-y-8' },
          bubble2: { size: 40, className: 'bottom-0 right-0 translate-x-5 translate-y-5' },
        },
      };
    }

    if (count > 0) {
      const isLearn = type === 'learn';
      return {
        state: 'available',
        ctaText: isLearn ? 'Start Learning' : 'Start Reviews',
        ctaIcon: '→',
        textColor: 'text-white',
        gradientFrom: isLearn ? 'var(--color-session-learn)' : 'var(--color-session-review)',
        gradientTo: isLearn ? 'var(--color-session-learn-accent)' : 'var(--color-session-review-accent)',
        bubbleStyle: isLearn
          ? {
              bubble1: { size: 88, className: 'top-1/2 right-0 translate-x-12 -translate-y-1/2' },
              bubble2: { size: 56, className: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-10' },
            }
          : {
              bubble1: { size: 76, className: 'top-0 right-1/2 translate-x-1/2 -translate-y-12' },
              bubble2: { size: 44, className: 'bottom-0 left-0 -translate-x-6 translate-y-6' },
            },
      };
    }

    if (showUpgradePrompt && type === 'learn') {
      return {
        state: 'upgrade',
        ctaText: 'Upgrade',
        ctaIcon: '→',
        textColor: 'text-white',
        gradientFrom: 'var(--color-session-upgrade)',
        gradientTo: 'var(--color-session-upgrade-accent)',
        bubbleStyle: {
          bubble1: { size: 110, className: 'top-0 right-0 translate-x-8 -translate-y-12' },
          bubble2: { size: 70, className: 'bottom-0 left-0 -translate-x-6 translate-y-8' },
        },
      };
    }

    return {
      state: 'disabled',
      ctaText: disabledText,
      ctaIcon: null,
      textColor: 'text-content-secondary',
      bubbleStyle: {
        bubble1: { size: 64, className: 'top-0 left-1/2 -translate-x-1/2 -translate-y-8' },
        bubble2: { size: 36, className: 'bottom-0 right-0 translate-x-4 translate-y-4' },
      },
    };
  };

  const config = getStateConfig();

  // Get the appropriate image key based on state
  const getImageKey = () => {
    if (onVacation) return 'ACTION_VACATION';
    if (type === 'learn') {
      return isActionable ? 'ACTION_LEARN_READY' : 'ACTION_LEARN_DONE';
    }
    return isActionable ? 'ACTION_REVIEW_READY' : 'ACTION_REVIEW_DONE';
  };

  const imageKey = getImageKey();
  const isExtraStudy = !suppressExtraStudy && config.state === 'disabled' && !onVacation && type === 'learn' && learnedToday < DAILY_LEARN_HARD_CAP;
  const isUpgrade = config.state === 'upgrade';

  // ─── Mobile compact row ───────────────────────────────────────────────────

  const mobileContent = (
    <div
      className={`flex items-center gap-3 pl-4 pr-6 py-4 rounded-2xl border-2 border-border overflow-hidden ${config.textColor} ${isActionable || isExtraStudy || isUpgrade ? 'active:scale-[.98] transition-transform duration-150' : ''}`}
      style={{
        background:
          config.state === 'disabled'
            ? 'var(--color-background-secondary)'
            : `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
        boxShadow: isActionable || onVacation || isUpgrade ? `0 4px 16px ${config.gradientFrom}30` : undefined,
      }}
    >
      <OptimizedImage
        imageKey={imageKey}
        className={`h-12 w-12 flex-shrink-0 ${config.state === 'disabled' ? 'grayscale opacity-40 dark:opacity-50' : ''}`}
      />
      <div className="flex-grow min-w-0">
        <h3 className="font-bold text-sm leading-tight">{title}</h3>
        <p className="text-xs opacity-80 leading-snug">{description}</p>
      </div>
      {onVacation || isEmailUnverified ? (
        <div className="flex-shrink-0 text-right">
          <span className="text-lg leading-none">{config.ctaIcon}</span>
          <p className="text-xs font-semibold opacity-90 leading-tight mt-0.5 whitespace-nowrap">{config.ctaText}</p>
        </div>
      ) : isUpgrade ? (
        <span className="font-semibold text-sm flex-shrink-0 text-white drop-shadow-sm">Upgrade →</span>
      ) : isExtraStudy ? (
        <Link
          to="/session/learn"
          state={{ isExtraStudy: true }}
          className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border"
          style={{
            background: 'linear-gradient(135deg, var(--color-session-learn)18, var(--color-session-learn-accent)18)',
            borderColor: 'var(--color-session-learn)',
            color: 'var(--color-session-learn)',
          }}
        >
          Extra Study →
        </Link>
      ) : config.state === 'disabled' ? (
        <span className="text-xs font-semibold opacity-80 leading-tight text-right flex-shrink-0 max-w-[140px]">{disabledText}</span>
      ) : (
        <div className="text-2xl font-black flex-shrink-0">{count}</div>
      )}
    </div>
  );

  const mobileCard = (() => {
    const inner = <div className={!isActionable && !isUpgrade ? 'cursor-not-allowed' : ''}>{mobileContent}</div>;
    if (config.state === 'available') return <Link to={link}>{inner}</Link>;
    if (config.state === 'upgrade') return <Link to="/user/subscription">{inner}</Link>;
    if (config.state === 'unverified') return <Tippy content="Please verify your email to start a session.">{inner}</Tippy>;
    if (config.state === 'disabled' && disabledTooltip) return <Tippy content={disabledTooltip}>{inner}</Tippy>;
    return inner;
  })();

  // ─── Desktop full card ────────────────────────────────────────────────────

  const cardClasses = `
    relative h-full flex flex-col p-6 rounded-2xl backdrop-blur-sm border-2 border-border
    overflow-hidden group
    transition-all duration-200 ease-out
    ${config.textColor}
    ${isActionable || isUpgrade ? 'hover:shadow-2xl cursor-pointer hover:-translate-y-2 active:scale-[.98]' : 'cursor-default'}
    ${config.state === 'disabled' ? 'bg-background-secondary/30 dark:bg-background-secondary/50' : ''}
  `;

  const CardContent = () => (
    <div
      className={cardClasses}
      style={{
        background:
          config.state === 'disabled'
            ? 'var(--color-background-secondary)'
            : `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
        backdropFilter: config.state === 'disabled' ? 'none' : 'blur(10px)',
        boxShadow: isActionable || onVacation || isUpgrade ? `0 8px 32px ${config.gradientFrom}30` : undefined,
      }}
    >
      {config.state !== 'disabled' && (
        <div
          className={`absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70 ${
            isActionable || isUpgrade ? 'group-hover:opacity-90' : ''
          } transition-opacity duration-300`}
        />
      )}

      {config.state !== 'disabled' && (
        <div className="absolute inset-0 opacity-30">
          <div
            className={`absolute rounded-full ${config.bubbleStyle.bubble1.className} ${
              isActionable || isUpgrade ? 'group-hover:scale-110' : ''
            } transition-transform duration-700`}
            style={{
              background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
              width: `${config.bubbleStyle.bubble1.size}px`,
              height: `${config.bubbleStyle.bubble1.size}px`,
              filter: 'brightness(1.2) saturate(1.3)',
            }}
          />
          <div
            className={`absolute rounded-full ${config.bubbleStyle.bubble2.className} ${
              isActionable || isUpgrade ? 'group-hover:scale-110' : ''
            } transition-transform duration-700`}
            style={{
              background: `linear-gradient(45deg, ${config.gradientFrom}, ${config.gradientTo})`,
              width: `${config.bubbleStyle.bubble2.size}px`,
              height: `${config.bubbleStyle.bubble2.size}px`,
              filter: 'brightness(1.3) saturate(1.4)',
            }}
          />
        </div>
      )}

      {config.state === 'disabled' && (
        <div className="absolute inset-0 opacity-8 dark:opacity-5">
          <div
            className={`absolute rounded-full ${config.bubbleStyle.bubble1.className}`}
            style={{
              background: 'var(--color-border)',
              width: `${config.bubbleStyle.bubble1.size}px`,
              height: `${config.bubbleStyle.bubble1.size}px`,
            }}
          />
          <div
            className={`absolute rounded-full ${config.bubbleStyle.bubble2.className}`}
            style={{
              background: 'var(--color-border)',
              width: `${config.bubbleStyle.bubble2.size}px`,
              height: `${config.bubbleStyle.bubble2.size}px`,
            }}
          />
        </div>
      )}

      <div className="absolute top-4 right-4 z-10">
        <div className="relative">
          {(isActionable || onVacation || isUpgrade) && (
            <div
              className="absolute inset-0 rounded-full blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-300"
              style={{
                background: `radial-gradient(circle, ${config.gradientFrom}, ${config.gradientTo}80, transparent 70%)`,
                transform: 'scale(1.4)',
              }}
            />
          )}
          <OptimizedImage
            imageKey={imageKey}
            className={`
              relative h-16 w-16 transition-all duration-300
              ${isActionable || isUpgrade ? 'group-hover:scale-110 drop-shadow-2xl' : ''}
              ${config.state === 'disabled' ? 'grayscale opacity-40 dark:opacity-50' : 'drop-shadow-lg'}
            `}
            style={{
              filter: isActionable || onVacation || isUpgrade ? 'brightness(1.1) contrast(1.1)' : undefined,
            }}
          />
        </div>
      </div>

      <div className="relative z-10 flex-grow">
        <div className="mb-4">
          <h3 className={`text-xl font-bold mb-2 transition-transform duration-300 leading-tight drop-shadow-sm`}>{title}</h3>
          <p className="opacity-90 text-sm leading-relaxed font-medium drop-shadow-sm">{description}</p>
        </div>
      </div>

      <div className="relative z-10 mt-6 flex items-end justify-between">
        {!onVacation && !isUpgrade && (
          <div className="flex flex-col">
            <div className={`text-3xl font-black leading-none mb-1 transition-transform duration-300 drop-shadow-sm`}>{count}</div>
            <div className="text-xs opacity-80 font-semibold uppercase tracking-wide drop-shadow-sm">items</div>
          </div>
        )}

        {isUpgrade && (
          <div className="flex flex-col">
            <div className="text-sm font-bold opacity-90 drop-shadow-sm">N4 · N3 · N2 · N1</div>
            <div className="text-xs opacity-70 font-semibold uppercase tracking-wide drop-shadow-sm">Premium only</div>
          </div>
        )}

        {isExtraStudy ? (
          <Link
            to="/session/learn"
            state={{ isExtraStudy: true }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all duration-200 hover:-translate-y-1 active:scale-95 hover:shadow-md"
            style={{
              background: 'linear-gradient(135deg, var(--color-session-learn)18, var(--color-session-learn-accent)18)',
              borderColor: 'var(--color-session-learn)',
              color: 'var(--color-session-learn)',
            }}
          >
            Extra Study →
          </Link>
        ) : (
          <div
            className={`
              font-semibold text-sm transition-all duration-300 drop-shadow-sm
              ${onVacation ? 'mx-auto text-base' : ''}
            `}
          >
            <span className="flex items-center gap-2">
              {config.ctaText}
              <span className="text-base">{config.ctaIcon}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const cardElement = <div className={`h-full ${!isActionable && !isUpgrade ? 'cursor-not-allowed' : ''}`}>{CardContent()}</div>;

  const desktopCard = (() => {
    if (config.state === 'available') return <Link className="block h-full" to={link}>{cardElement}</Link>;
    if (config.state === 'upgrade') return <Link className="block h-full" to="/user/subscription">{cardElement}</Link>;
    if (config.state === 'unverified') return <Tippy content="Please verify your email to start a session.">{cardElement}</Tippy>;
    if (config.state === 'disabled' && disabledTooltip) return <Tippy content={disabledTooltip}>{cardElement}</Tippy>;
    return cardElement;
  })();

  return (
    <>
      <div className="sm:hidden">{mobileCard}</div>
      <div className="hidden sm:block h-full">{desktopCard}</div>
    </>
  );
};

export default ActionCard;